import * as _ from 'lodash';
import { formatDate } from '@angular/common';

/**
 * Query building for the many-to-one InlineSearch header (setting InlineSearch).
 * Mirrors what SemDialogComponent sends (projection, ClassTypes, AdditionalFilterQuery with ${...}
 * interpolation, repositories, api/data/relatedentity URL) so the inline field and the dialog find
 * and resolve the same records. SemDialogComponent is intentionally left untouched.
 */

export interface InlineHit {
  model: any;
  title: string;
  subtitle: string;
}

/** Lower-case, strip diacritics (č -> c, đ -> d) so "vucedol" matches "Vučedol". */
export function foldText(value: any): string {
  if (value == null) {
    return "";
  }
  return ("" + value)
    .toLowerCase()
    .replace(/đ/g, "d")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/** Every typed word must start a word of the text (word-start, diacritic-insensitive). */
export function matchesWordStart(text: string, typed: string): boolean {
  const words = foldText(text).split(/[^a-z0-9]+/).filter(w => w.length > 0);
  const tokens = foldText(typed).split(/[^a-z0-9]+/).filter(t => t.length > 0);
  return tokens.every(t => words.some(w => w.startsWith(t)));
}

/**
 * Keeps only the hits that still match the typed text (word start, diacritic-insensitive) on one of the
 * searched properties. Used on every keystroke so the open list never shows results for the previous text
 * while the next request is in flight.
 */
export function narrowHits(hits: InlineHit[], typed: string, props: string[], getValue: (o: any, p: string) => any): InlineHit[] {
  if ((typed || "").trim().length == 0) {
    return hits;
  }
  return hits.filter(h => props.some(p => matchesWordStart("" + (getValue(h.model, p) ?? ""), typed)));
}

export function defaultDisplayFields(displayFields: any[], translate: (key: string) => string): any[] {
  if (displayFields == null || typeof displayFields == "undefined" || displayFields.length == 0) {
    return [{ "Title": translate("Title"), "PropertyName": "Title", "Type": "Text" }];
  }
  return displayFields;
}

/**
 * Builds the search request. The typed text is OR-ed over the text SearchFields (Date/CB fields are
 * skipped: one text box cannot fill a range or a checkbox); without SearchFields it searches Title.
 * Only the first typed word goes to the server as a prefix query; the rest is matched client-side.
 */
export function buildInlineSearchQuery(opts: {
  entityModel: string;
  searchFields: any[];
  displayFields: any[];
  additionalSearchFilter: string;
  currentFormModel: any;
  repositories: string;
  typed: string;
  pageSize: number;
}): any {

  const fields = (opts.searchFields || []).filter(f => f && f.Type != 'Date' && f.Type != 'CB');
  const fieldNames = fields.length > 0 ? fields.map(f => f.PropertyName) : ["Title"];

  const tokens = (opts.typed || "").trim().split(/\s+/).filter(t => t.length > 0);
  let treeQuery: any;

  if (tokens.length == 0) {
    treeQuery = { "Type": "FieldRange", "FieldName": "Id", "FieldValueFrom": 1, "FieldValueTo": "*" };
  } else {
    // Same "/" rule as the dialog: values with "/" are searched exactly.
    const first = tokens[0];
    const value = first.indexOf("/") >= 0 ? first : first + "*";
    treeQuery = { "Type": "OR", "SubQueries": fieldNames.map(n => ({ "Type": "FieldValue", "FieldName": n, "FieldValue": value })) };
  }

  if (opts.additionalSearchFilter != null && typeof opts.additionalSearchFilter != "undefined" && opts.additionalSearchFilter.length > 0) {
    try {
      if (opts.currentFormModel != null && typeof opts.currentFormModel != "undefined") {
        const compiled = _.template(opts.additionalSearchFilter, { interpolate: /\${([\s\S]+?)}/g });
        const compiledSearchQuery = compiled(opts.currentFormModel);
        if (compiledSearchQuery != null && typeof compiledSearchQuery != "undefined") {
          treeQuery = { Type: "AND", SubQueries: [JSON.parse(compiledSearchQuery), treeQuery] };
        }
      }
    } catch {
    }
  }

  const projection = (opts.displayFields || []).map(f => f.PropertyName);
  fieldNames.forEach(n => { if (!projection.includes(n)) { projection.push(n); } });
  projection.push("Id", "SourceRepository", "RepositoryId");

  const classTypes = ((opts.entityModel || "") + "").split(",").map(m => m.trim()).filter(m => m.length > 0);

  return {
    "MainQuery": null,
    "FieldQueries": {},
    "FacetQueries": {},
    "RequestedFacets": null,
    "Projection": projection,
    "TreeQuery": treeQuery,
    "ClassTypes": classTypes,
    "Page": "1",
    "PageSize": opts.pageSize,
    "RepositoryName": opts.repositories != null && opts.repositories.length > 0 ? opts.repositories : "lucene",
    "Form": null,
    "UseCache": false
  };
}

/** Same URL SemDialogComponent uses to resolve a hit into the full related-entity model. */
export function buildRelatedEntityUrl(formName: string, propName: string, entityModel: string, hit: any): string {
  const firstModel = ((entityModel || "") + "").split(",").map(m => m.trim()).filter(m => m.length > 0)[0] || entityModel;
  const prop = propName && propName.length > 0 ? propName : "_";
  if (hit["Id"] > 0) {
    return "api/data/relatedentity/" + formName + "/" + prop + "/" + firstModel + "/" + hit["Id"];
  }
  return "api/data/relatedentity/external/" + formName + "/" + propName + "/" + hit["SourceRepository"] + "/" + hit["RepositoryId"];
}

/** Date display fields: same pattern and zone as the dialog grid (sem-dialog.component.html `date:'dd.MM.yyyy':'CET'`). */
export const INLINE_DATE_FORMAT = 'dd.MM.yyyy';
export const INLINE_DATE_TIMEZONE = 'CET';

/** One display value as text: Date fields formatted like the dialog grid, an unparsable date kept as is. */
export function displayFieldText(v: any, type: string): string {
  if (v == null || v === "") return "";
  if (type == "Date" && (typeof v == "string" || v instanceof Date)) {
    try { return formatDate(v, INLINE_DATE_FORMAT, 'en-US', INLINE_DATE_TIMEZONE); } catch { return typeof v == "string" ? v : ""; }
  }
  return typeof v == "object" ? "" : ("" + v);
}

/** Title = first DisplayTableFields entry, subtitle = the rest joined with " · ". */
export function toHit(model: any, displayFields: any[], getValue: (o: any, p: string) => any): InlineHit {
  const values = displayFields.map(f => displayFieldText(getValue(model, f.PropertyName), f.Type));
  return { model: model, title: values[0] || "", subtitle: values.slice(1).filter(v => v.length > 0).join(" · ") };
}

// ---- Multi-select under InlineSearch ("add to list") ----

type GetValue = (obj: any, path: string) => any;
type Assign = (obj: any, path: string, value: any, create: boolean) => any;

/** Ids of the entities already linked in the one-to-many target rows. */
export function linkedEntityIds(targetArray: any[], entityName: string, getValue: GetValue): any[] {
  if (!Array.isArray(targetArray)) {
    return [];
  }
  return targetArray
    .map(row => getValue(row, entityName))
    .filter(e => e && typeof e == "object" && e.Id)
    .map(e => e.Id);
}

/** Index of the target row that links entity `id`, -1 if none. */
export function findLinkedRowIndex(targetArray: any[], entityName: string, id: any, getValue: GetValue): number {
  if (!Array.isArray(targetArray) || id == null) {
    return -1;
  }
  return targetArray.findIndex(row => {
    const e = getValue(row, entityName);
    return !!(e && typeof e == "object" && e.Id && e.Id == id);
  });
}

/**
 * Appends one row ({ [entityName]: entity }) per entity not yet linked. Shared by the dialog sync
 * (applyMultiSelectResult) and the inline add-only path. Returns the entities actually added.
 */
export function appendLinkedRows(targetArray: any[], entities: any[], entityName: string, getValue: GetValue, assign: Assign): any[] {
  const existingIds = linkedEntityIds(targetArray, entityName, getValue);
  const added: any[] = [];
  for (const item of entities || []) {
    if (item == null || existingIds.includes(item.Id)) {
      continue;
    }
    targetArray.push(assign({}, entityName, item, true));
    existingIds.push(item.Id);
    added.push(item);
  }
  return added;
}

/**
 * Add-only result (InlineSearch + EnableMultiSelect): the dialog's unticked entities are NOT removed.
 * Drops the row the control sits on when nothing is linked on it yet (same as the dialog sync), then appends.
 */
export function applyAddOnly(targetArray: any[], currentRow: any, entities: any[], entityName: string, getValue: GetValue, assign: Assign): any[] {
  const current = currentRow != null ? getValue(currentRow, entityName) : null;
  const currentEmpty = !current || typeof current !== "object" || !current.Id;
  if (currentEmpty && currentRow != null) {
    const idx = targetArray.indexOf(currentRow);
    if (idx > -1) {
      targetArray.splice(idx, 1);
    }
  }
  return appendLinkedRows(targetArray, entities, entityName, getValue, assign);
}

/**
 * Inline "add to list" pick: the inline field lives on an empty row (the o2m template row). That row is kept
 * (same object, moved to the end) and the picked entities are inserted before it, so the field component, its focus
 * and its open list survive the pick. A field on a filled row appends at the end like appendLinkedRows.
 */
export function applyAddKeepingField(targetArray: any[], currentRow: any, entities: any[], entityName: string, getValue: GetValue, assign: Assign): any[] {
  const current = currentRow != null ? getValue(currentRow, entityName) : null;
  const currentEmpty = !current || typeof current !== "object" || !current.Id;
  const idx = currentEmpty && currentRow != null ? targetArray.indexOf(currentRow) : -1;
  if (idx > -1) {
    targetArray.splice(idx, 1);
  }
  const added = appendLinkedRows(targetArray, entities, entityName, getValue, assign);
  if (idx > -1) {
    targetArray.push(currentRow);
  }
  return added;
}

// ---- Linked fields: auto-hide + collapsed section ----

/** Binding of a rendered control: form-level hosts carry name="model.<binding>", o2m row controls name="<binding>". */
export function bindingOfElementName(name: string): string {
  if (!name) {
    return "";
  }
  return name.startsWith("model.") ? name.substring("model.".length) : name;
}

/** Linked fields of an m2o = bindings under `<entityName>.` (children of the container or sibling rows of the tab). */
export function linkedFieldBindings(bindings: string[], entityName: string): string[] {
  if (!entityName) {
    return [];
  }
  const prefix = entityName + ".";
  return (bindings || []).filter(b => typeof b == "string" && b.startsWith(prefix) && b.length > prefix.length);
}

/** The binding that repeats the picker's title: `<entityName>.<first DisplayTableFields PropertyName>`. */
export function titleBinding(entityName: string, displayFields: any[]): string {
  const first = (displayFields || []).find(f => f && f.PropertyName);
  const prop = first ? first.PropertyName : "Title";
  return entityName + "." + prop;
}

/**
 * Which linked field bindings to hide.
 * - single picker: none – the linked fields render like under the old control (#2855);
 * - multi picker (EnableMultiSelect): the one equal to the title binding when the row has a value or is a plain o2m row.
 * Fields not under `<entityName>.` are never returned.
 */
export function bindingsToHide(bindings: string[], entityName: string, displayFields: any[], hasValue: boolean, multi: boolean = false, plainRow: boolean = false): { hide: string[], collapsed: boolean } {
  const linked = linkedFieldBindings(bindings, entityName);
  const unique = Array.from(new Set(linked));
  if (multi) {
    // EnableMultiSelect: the picker lives in each o2m row; a row with a picked value repeats the name. In a plain o2m row the empty
    // add row hides it too (the picker alone, like a single picker's row); grid row editors keep it. The add row is never collapsed.
    const title = titleBinding(entityName, displayFields);
    return { hide: hasValue || plainRow ? unique.filter(b => b == title) : [], collapsed: false };
  }
  // #2855: a single picker shows its linked fields exactly like the old control (no collapse, no hidden title field) –
  // except a plain o2m row that holds only the picker and its title field (Galis Vodje delavnic): the picker is the row.
  const title = titleBinding(entityName, displayFields);
  if (plainRow && unique.length == 1 && unique[0] == title) {
    return { hide: unique, collapsed: false };
  }
  return { hide: [], collapsed: false };
}

/**
 * (form link | binding) -> entity name of the o2m targets where an inline multi picker keeps an empty add-field row.
 * Filled only by InlineSearch + EnableMultiSelect pickers; empty for every other form, so pruning is a no-op there.
 */
const inlineAddRowTargets = new Map<string, string>();

export function registerInlineAddRows(formLink: string, binding: string, entityName: string): void {
  if (formLink && binding && entityName) {
    inlineAddRowTargets.set(formLink + "|" + binding, entityName);
  }
}

/**
 * Removes the unsaved rows without a linked entity from the registered targets of `model` (call it on the POST copy).
 * Saved rows (Id > 0) are always kept. Returns the number of removed rows.
 */
export function pruneInlineAddRows(model: any, getValue: (o: any, p: string) => any): number {
  if (!model || inlineAddRowTargets.size == 0) {
    return 0;
  }
  const link = model["SerializationObjectFormLink"];
  let removed = 0;
  inlineAddRowTargets.forEach((entityName, key) => {
    const sep = key.lastIndexOf("|");
    if (key.substring(0, sep) != link) {
      return;
    }
    const rows = getValue(model, key.substring(sep + 1));
    if (!Array.isArray(rows)) {
      return;
    }
    for (let i = rows.length - 1; i >= 0; i--) {
      const row = rows[i];
      const entity = row ? getValue(row, entityName) : null;
      if (!(row && row.Id) && !(entity && entity.Id)) {
        rows.splice(i, 1);
        removed++;
      }
    }
  });
  return removed;
}

/** Value text of a picked record: its first display value, else the placeholder (never the record id). */
export function inlineDisplayText(title: string, placeholder: string): string {
  return title ? title : placeholder;
}

/**
 * 'row': the picker sits in a plain o2m row (Galis Vodje delavnic, Avtorji delavnice) – each person is one flat row and
 * × removes the row. 'field': form control, or the row editor of an inline-edit grid (OKS Storitve) – unchanged.
 */
export function inlineRowMode(inO2mRow: boolean, inGrid: boolean): 'row' | 'field' {
  return inO2mRow && !inGrid ? 'row' : 'field';
}

/** Row menu (⋮) of a row the picker owns goes when it only deletes: on a row with a value (× deletes) and on the empty multi add row. */
export function inlineHideRowMenu(owns: boolean, hasValue: boolean, multi: boolean, onlyDelete: boolean): boolean {
  return owns && onlyDelete;
}

/** An empty single picker row it owns gets a ×: with ⋮ gone that is its delete. The multi add row stays (it adds the next person). */
export function inlineEmptyRowClear(owns: boolean, hasValue: boolean, multi: boolean): boolean {
  return owns && !hasValue && !multi;
}

/** "Nov zapis" while this picker's own row is still empty: focus it instead of stacking a second empty row. */
export function inlineRedirectAddNew(owns: boolean, hasValue: boolean, canEdit: boolean): boolean {
  return owns && !hasValue && canEdit;
}

/** Removes the row object from the o2m list in place (no dialog: nothing is stored before Shrani). Index removed or -1. */
export function inlineRemoveRow(values: any[], section: any): number {
  if (!Array.isArray(values)) {
    return -1;
  }
  let i = values.indexOf(section);
  // a saved row may be held as another copy of the same entry (form reloaded on UREDI): match it by Id
  if (i < 0 && section && section.Id > 0) {
    i = values.findIndex(v => v && v.Id === section.Id);
  }
  if (i > -1) {
    values.splice(i, 1);
  }
  return i;
}

/**
 * × removes the whole o2m row (instead of clearing the value) when the picker owns a plain row, or when it is a multi
 * picker in a plain row: that row only exists to hold the picked entity, an emptied one is posted with a null link.
 */
export function inlineClearRemovesRow(inRow: boolean, inGrid: boolean, owns: boolean, multi: boolean): boolean {
  return inRow && !inGrid && (owns || multi);
}

/**
 * Layout row whose sibling column we hid: the one column still visible takes the row (BRD Koordinatna preciznost
 * beside the hidden Lokalno ime). Only same-class siblings count; clearfix-like helpers are ignored. Index or -1.
 */
export function inlineWidenColumn(children: { cls: string, shown: boolean, ours: boolean }[]): number {
  const ours = children.filter(c => !c.shown && c.ours);
  if (ours.length == 0) {
    return -1;
  }
  const cls = ours[0].cls;
  const shown = children.map((c, i) => ({ c, i })).filter(x => x.c.shown && x.c.cls == cls);
  return shown.length == 1 ? shown[0].i : -1;
}
